import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { KeyRound } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Mandatory password-change modal. Rendered when the logged-in user has
 * `must_change_password === true`. Cannot be dismissed; user must complete
 * the change (or log out from another mechanism) to leave.
 */
export default function ForcePasswordResetModal({ open, username, onComplete, onLogout }) {
  const [current, setCurrent] = useState('');
  const [next, setNext]       = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy]       = useState(false);

  const submit = async () => {
    if (next.length < 8) return toast.error('New password must be at least 8 characters');
    if (next !== confirm) return toast.error('New password and confirmation do not match');
    if (next === current) return toast.error('New password must be different from current');
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`,
        { current_password: current, new_password: next },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success('Password updated. Continuing to app…');
      onComplete?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Password change failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* not dismissible */ }}>
      <DialogContent className="max-w-md [&>button]:hidden" onEscapeKeyDown={e => e.preventDefault()} onPointerDownOutside={e => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" /> Set a new password
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2 mb-2">
          {username ? <>Welcome, <span className="font-mono">{username}</span>. </> : null}
          For security, you must change your password before continuing.
        </p>
        <div className="grid gap-3">
          <div><Label>Current password</Label><Input type="password" value={current} onChange={e => setCurrent(e.target.value)} data-testid="force-current-pwd" autoFocus /></div>
          <div><Label>New password</Label><Input type="password" value={next} onChange={e => setNext(e.target.value)} placeholder="min 8 chars" data-testid="force-new-pwd" /></div>
          <div><Label>Confirm new password</Label><Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} data-testid="force-confirm-pwd" /></div>
        </div>
        <div className="flex justify-between items-center mt-4">
          <Button variant="ghost" size="sm" onClick={onLogout} data-testid="force-logout-btn">Log out instead</Button>
          <Button onClick={submit} disabled={busy || !current || !next || !confirm} data-testid="force-submit">
            {busy ? 'Updating…' : 'Update password'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
